/**
 * Modal — the app's one dialog primitive: a fixed full-screen overlay with a
 * centered `.panel` body. Closes on Escape and on a backdrop click (clicks
 * inside the body don't propagate out). Content is caller-supplied; the modal
 * only owns the chrome.
 */
import { useEffect, type ReactNode } from 'react';

export interface ModalProps {
  /** Optional heading rendered as the panel title. */
  title?: string;
  /** Invoked on Escape, backdrop click — the caller unmounts the modal. */
  onClose: () => void;
  children: ReactNode;
}

export function Modal({ title, onClose, children }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="modal-overlay">
      <div
        className="panel modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="modal"
        onClick={(e) => e.stopPropagation()}
      >
        {title && <h2 className="panel__title">{title}</h2>}
        {children}
      </div>
    </div>
  );
}
